// src/features/fourlink-config/components/blocks/Block1Steps/EquiposStep.jsx

import React, { useMemo, useState } from 'react';
import { CommentButton } from '../../shared/CommentButton';

export const EquiposStep = ({
  equiposDisponibles,
  hiddenEquipos,
  selectedEquipos,
  equiposConfig,
  onToggleEquipo,
  onAddEquipo,
  onUpdateQRName,
  onToggleQRActive,
  onHideEquipos,
  onRestoreEquipo,
  onCommentClick,
  hasComment,
  getEquipoComment,
}) => {
  const equiposSectionKey = 'section:equipos';
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [newEquipoName, setNewEquipoName] = useState('');
  const [equiposToHide, setEquiposToHide] = useState([]);

  const equiposVisibles = useMemo(
    () => (Array.isArray(equiposDisponibles) ? equiposDisponibles : []),
    [equiposDisponibles],
  );

  const hiddenList = useMemo(
    () => (Array.isArray(hiddenEquipos) ? hiddenEquipos : []),
    [hiddenEquipos],
  );

  const toggleEquipoToHide = (equipo) => {
    setEquiposToHide((prev) =>
      prev.includes(equipo) ? prev.filter((t) => t !== equipo) : [...prev, equipo],
    );
  };

  const handleConfirmHide = () => {
    if (typeof onHideEquipos === 'function' && equiposToHide.length > 0) {
      onHideEquipos(equiposToHide);
    }
    setEquiposToHide([]);
    setIsHideModalOpen(false);
  };

  const handleCreateEquipo = (e) => {
    e.preventDefault();
    if (!newEquipoName.trim()) return;
    const created = typeof onAddEquipo === 'function'
      ? onAddEquipo(newEquipoName.trim())
      : false;
    if (created === false) return;
    setNewEquipoName('');
    setIsAddModalOpen(false);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewEquipoName('');
  };

  const handleCloseHideModal = () => {
    setIsHideModalOpen(false);
    setEquiposToHide([]);
  };

  const renderModalShell = (children) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
        {children}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-2xl font-bold text-gray-800">Equipos</h2>
        <div className="flex items-center gap-3">
          {hasComment && (
            <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
          )}
          <button
            type="button"
            onClick={() => setIsHideModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-red-200 text-red-700 bg-white hover:bg-red-50 transition-colors font-semibold"
            title="Ocultar equipo"
          >
            <span className="sr-only">Ocultar equipo</span>
            <span className="text-lg leading-none font-bold">-</span>
          </button>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-green-200 text-green-700 bg-white hover:bg-green-50 transition-colors"
            title="Añadir nuevo equipo"
          >
            <span className="text-lg leading-none font-bold">+</span>
            <span className="sr-only">Anadir equipo</span>
          </button>
          <CommentButton
            onClick={() => onCommentClick({ key: equiposSectionKey, label: 'Equipos' })}
            hasComment={hasComment}
            srLabel="Añadir comentario para Equipos"
            variant="circle"
            className="border-blue-200 text-blue-700 bg-white hover:bg-blue-50"
          />
        </div>
      </div>
      <p className="text-gray-600 mb-6">Selecciona los equipos que aplican y configura sus estaciones de trabajo (QRs):</p>

      <div className="grid grid-cols-1 gap-6">
        {Array.isArray(equiposVisibles) && equiposVisibles.length > 0 ? equiposVisibles.map(equipo => {
          const equipoCommentKey = `equipo:${equipo}`;
          const equipoHasComment = getEquipoComment(equipo);
          
          return (
            <div key={equipo} className="border border-gray-200 rounded-xl p-6 bg-white hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    checked={selectedEquipos.includes(equipo)}
                    onChange={() => onToggleEquipo(equipo)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-gray-800">{equipo}</h3>
                    {selectedEquipos.includes(equipo) && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                        <svg className="w-4 h-4 text-green-600 inline-block" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Activo
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {equipoHasComment && (
                    <span className="text-xs font-semibold text-blue-700">Comentario guardado</span>
                  )}
                  <CommentButton
                    onClick={() => onCommentClick({ key: equipoCommentKey, label: `Equipo ${equipo}` })}
                    hasComment={equipoHasComment}
                    srLabel={`${equipoHasComment ? 'Editar' : 'Añadir'} comentario para el equipo ${equipo}`}
                  />
                </div>
              </div>

              {selectedEquipos.includes(equipo) && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-3">
                      Estaciones de Trabajo (QRs)
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Configura hasta 5 estaciones QR para este equipo. Activa solo las que necesites.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {(equiposConfig[equipo]?.qrs || []).map((qr, index) => (
                      <div 
                        key={qr.id} 
                        className={`border-2 rounded-lg p-3 transition-all ${
                          qr.active 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={qr.active}
                            onChange={() => onToggleQRActive(equipo, index)}
                            className="w-3 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={`text-xs font-medium ${
                            qr.active ? 'text-blue-800' : 'text-gray-500'
                          }`}>
                            QR {qr.id}
                          </span>
                        </div>
                        
                        <input
                          type="text"
                          value={qr.name}
                          onChange={(e) => onUpdateQRName(equipo, index, e.target.value)}
                          placeholder={`QR ${qr.id}`}
                          disabled={!qr.active}
                          className={`w-full px-2 py-1 border border-gray-300 rounded text-xs transition-all ${
                            qr.active 
                              ? 'focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white' 
                              : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          }`}
                        />
                        
                        {qr.active && (
                          <div className="mt-1 text-center">
                            <svg className="w-4 h-4 text-green-600 mx-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">Estaciones activas:</span>
                      <span className="text-blue-600 font-bold">
                        {(equiposConfig[equipo]?.qrs || []).filter(qr => qr.active).length} de 5
                      </span>
                    </div>
                    {(equiposConfig[equipo]?.qrs || []).filter(qr => qr.active).length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        <strong>QRs activos:</strong> {' '}
                        {(equiposConfig[equipo]?.qrs || [])
                          .filter(qr => qr.active)
                          .map(qr => qr.name)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }) : (
          <div className="p-4 border rounded-lg bg-gray-50 text-gray-600">
            No hay equipos disponibles.
          </div>
        )}
      </div>

      {selectedEquipos.length > 0 && equiposConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">Resumen de Configuración</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{selectedEquipos.length}</div>
              <div className="text-sm text-blue-700">Equipos seleccionados</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {selectedEquipos.reduce((total, equipo) => 
                  total + (equiposConfig[equipo]?.qrs?.filter(qr => qr.active).length || 0), 0
                )}
              </div>
              <div className="text-sm text-green-700">QRs activos</div>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Añadir equipo</h3>
              <p className="text-sm text-gray-600">Crea nuevos equipos y revisa los que has ocultado.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={handleCloseAddModal}
              aria-label="Cerrar modal de añadir equipo"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleCreateEquipo} className="space-y-3">
            <label className="block text-sm font-medium text-gray-700" htmlFor="nuevo-equipo-input">
              Nombre del equipo
            </label>
            <input
              id="nuevo-equipo-input"
              type="text"
              value={newEquipoName}
              onChange={(e) => setNewEquipoName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ej. Cocina fría"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseAddModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={!newEquipoName.trim()}
              >
                Añadir equipo
              </button>
            </div>
          </form>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Equipos ocultos</h4>
            {hiddenList.length === 0 ? (
              <p className="text-sm text-gray-500">No hay equipos ocultos.</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {hiddenList.map((item) => (
                  <li key={item} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-800">{item}</span>
                    {typeof onRestoreEquipo === 'function' && (
                      <button
                        type="button"
                        onClick={() => onRestoreEquipo(item)}
                        className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        Restaurar
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isHideModalOpen && renderModalShell(
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Ocultar equipos</h3>
              <p className="text-sm text-gray-600">Selecciona los equipos que quieras ocultar temporalmente.</p>
            </div>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-800"
              onClick={handleCloseHideModal}
              aria-label="Cerrar modal de ocultar equipos"
            >
              ✕
            </button>
          </div>

          {equiposVisibles.length === 0 ? (
            <p className="text-sm text-gray-500">No hay equipos disponibles para ocultar.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {equiposVisibles.map((equipo) => (
                <label
                  key={equipo}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={equiposToHide.includes(equipo)}
                      onChange={() => toggleEquipoToHide(equipo)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-800">{equipo}</span>
                  </div>
                  {selectedEquipos.includes(equipo) && (
                    <span className="text-xs text-blue-600 font-medium">Seleccionado</span>
                  )}
                </label>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCloseHideModal}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmHide}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              disabled={equiposToHide.length === 0}
            >
              Ocultar seleccionados
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
